import React, {
  useCallback,
  useRef,
  useMemo,
  useState,
  useEffect
} from 'react';
import { any, object, shape, func, string } from 'prop-types';
import DirectBook from '@kyruus/direct-book-widget';
import DirectBookV2 from '@kyruus/direct-book-widget-v2';
import { availabilityTilesTracking } from '@kyruus/availability-tiles';
import {
  embeddedFrameDispatchers,
  EMBEDDED_PAYER_DOMAIN_PARAM
} from '@hsq/embedded-scheduling-communication';
import { DirectBookContainer } from './styles';
import {
  getThirdPartySupported,
  handleScrollToTop,
  getAppointmentEhrId
} from './utils';
import {
  getDefaultConfig,
  getMychartLoginConfig,
  getMychartEmbeddedConfig,
  getCernerConfig,
  getCrmConfig
} from './configs';
import {
  EHR_CERNER,
  EHR_MYCHART,
  EHR_MYCHART_EMBEDDED,
  EHR_NONE,
  EHR_CRM,
  PMC_DBW_ENABLED_PROPERTY,
  PMAC_DBW_ENABLED_PROPERTY,
  EHR_CERNER_SMART
} from './constants';
import { BOOKING_SUCCESS } from './tracking';
import { useReferralCrmConnector } from '../referral-crm-connector';
import { BOOK_TRACKING_PARAM_NAME } from '../tracking/constants';
import { useEhrData } from './useEhrData';
import {
  isModuleEnabled,
  MODULES,
  shouldRenderDirectBookInDrawer
} from 'Common/config';
import { getApptPurposeFilterProps } from '../shared/appt-purpose-filtering';
import { injectIntl } from '@kyruus/intl';
import { getSmartConfig } from './configs/getSmartConfig';

// whitelist of valid track_src params, to prevent garbage in the tracking data.
// we might want to revisit the whitelist approach when we get more possible values
const VALID_TRACK_SRC_PARAMS = [
  availabilityTilesTracking.TRACKING_SOURCE_PROFILE,
  availabilityTilesTracking.TRACKING_SOURCE_SEARCH
];

const propTypes = {
  // config containing `direct_book` direct booking config
  // This will contain things like messages for `react-intl`, button placement
  // form layout etc..
  config: object,

  // customerCode as set in redux
  customerCode: string,

  // react-router match prop
  match: shape({
    params: shape({
      providerId: string
    })
  }).isRequired,

  // react-router history controller
  history: shape({
    push: func,
    replace: func,
    location: shape({
      pathname: string,
      search: string
    })
  }).isRequired,

  // tokens store from pmc redux state
  tokens: shape({
    bcsToken: string
  }),

  // A ref storing the searchSDK class instance
  searchSDK: shape({
    current: any
  }).isRequired
};

/**
 * Returns props used on the direct book widget.
 * e.g: There are integrations like cerner where we want it
 * to route to a different location after booking.
 * @param {function} log
 * @param {Object} config
 * @param {string} csrfToken
 * @param {string} customerCode
 * @param {Object} props.location - the react-router location object
 * @param {Object} ehrData
 * @param {Object} tokens - tokens object as seen in redux
 * @param {Object} searchSDK
 * @returns DirectBook props for the current integration.
 */
const getDirectBookProps = (
  log,
  config,
  csrfToken,
  customerCode,
  location,
  ehrData,
  tokens,
  searchSDK,
  crmConnector
) => {
  // Check for third-party config; otherwise, use default
  const { type, featureFlags, loginConfig } = getThirdPartySupported(config);

  // Currently, we support only a single third-party login within the DB flow
  switch (type) {
    case EHR_MYCHART: {
      return getMychartLoginConfig({
        type,
        featureFlags,
        csrfToken,
        customerCode,
        location,
        loginConfig,
        ehrData,
        tokens,
        config,
        searchSDK,
        log,
        demographicLogMessages: {
          success: 'application_status.mychart_lookup_patient.success',
          failure: 'application_status.mychart_lookup_patient.failure'
        }
      });
    }
    case EHR_CERNER_SMART: {
      return getSmartConfig({
        type,
        featureFlags,
        csrfToken,
        customerCode,
        location,
        loginConfig,
        ehrData,
        tokens,
        config,
        searchSDK,
        log
      });
    }
    case EHR_MYCHART_EMBEDDED: {
      return getMychartEmbeddedConfig({
        type,
        featureFlags,
        csrfToken,
        customerCode,
        location,
        loginConfig,
        ehrData,
        tokens,
        config,
        searchSDK,
        log
      });
    }
    case EHR_CERNER: {
      return getCernerConfig({
        type,
        featureFlags,
        customerCode,
        location,
        csrfToken,
        tokens,
        config,
        searchSDK,
        log
      });
    }
    case EHR_CRM:
      return getCrmConfig({
        type,
        customerCode,
        location,
        tokens,
        config,
        searchSDK,
        crmConnector
      });
    case EHR_NONE:
    default: {
      return getDefaultConfig({
        type,
        customerCode,
        location,
        tokens,
        config,
        searchSDK
      });
    }
  }
};

// Payers app is appending the `window.location.origin` as a query param so embeddedFrameDispatchers
// are only dispatching back to that original host.
const getPayerOrigin = () => {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get(EMBEDDED_PAYER_DOMAIN_PARAM) || null;
};

function DirectBooking({
  csrfToken,
  config,
  customerCode,
  productName,
  match,
  log,
  history,
  location,
  tokens,
  searchSDK,
  intl,
  onScroll,
  searchSummary,
  appSettings
}) {
  // don't do server-side rendering of direct-book widget
  const [isClient, setIsClient] = useState(false);
  // useRef instead of useState as useState would cause a re-render, and we only use this value in a callback
  const trackingSourceContainer = useRef(null);

  useEffect(() => {
    // only executes after first client render
    setIsClient(true);
  }, []);

  // Payer Listener Setup
  //----------------------------------------------------------------------------
  useEffect(() => {
    const payerOrigin = getPayerOrigin();
    if (!payerOrigin) return;
    // Dispatches that the app has loaded
    // Loading can take a while on first connection, so Payers blocks the user from
    // navigating past the disclaimer until the app receives this signal.
    embeddedFrameDispatchers.dispatchOnLoad({ targetOrigin: payerOrigin });
    // Listens for the Escape key to ensure the user can close the Sidebar with escape.
    const escapeHandler = (e) => {
      if (e.key === 'Escape') {
        embeddedFrameDispatchers.dispatchOnEscape({
          targetOrigin: payerOrigin
        });
      }
    };
    window.document.body.addEventListener('keyup', escapeHandler);
    // Try to prevent the user from navigating out of booking
    //----------------------------------------------------------------------------
    // Delegated event handler to force `a` tags to open in new tabs.
    const handleClick = (evt) => {
      const linkElement = evt.target.closest('a');
      if (!linkElement) return;
      const { href, protocol, target } = linkElement;
      if (
        href &&
        target !== '_blank' &&
        // let protocols like `mailto` or `tel` proceed as normal
        ['http:', 'https:'].includes(protocol)
      ) {
        evt.preventDefault();
        window.open(href, '_blank');
      }
    };
    window.document.body.addEventListener('click', handleClick);
    return () => {
      // This should be an impossible occurence
      window.document.body.removeEventListener('click', handleClick);
      window.document.body.removeEventListener('keyup', escapeHandler);
    };
  }, []);
  // save the initial track_src param that was on the url when the component was mounted,
  // because navigating between steps within PMC has the potential to update the URL and the url
  // param could be removed by the time the patient gets to the final booking step.
  useEffect(() => {
    try {
      const queryParams = new URLSearchParams(window.location.search);
      const trackSrc = queryParams.get(BOOK_TRACKING_PARAM_NAME);
      if (trackSrc && VALID_TRACK_SRC_PARAMS.includes(trackSrc)) {
        trackingSourceContainer.current = trackSrc;
      }
    } catch (e) {
      // could not parse query params
    }
  }, []);

  const { enabled: crmEnabled, connector: crmConnector } =
    useReferralCrmConnector(config);

  const ehrData = useEhrData({ config });

  const directBookProps = useMemo(() => {
    // only return null when ehrData is cleared while in standalone mode
    if (!crmEnabled && !ehrData.data) {
      return null;
    }

    return getDirectBookProps(
      log,
      config,
      csrfToken,
      customerCode,
      location,
      ehrData,
      tokens,
      searchSDK,
      crmConnector
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config,
    csrfToken,
    customerCode,
    location,
    ehrData,
    tokens,
    searchSDK,
    crmConnector,
    crmEnabled,
    log
  ]);

  const handleDidBook = useCallback(
    ({ appointment, location, provider }) => {
      const eventData = {
        booking_source: trackingSourceContainer.current,
        patient_relationship: appointment.patient_relationship || ''
      };

      if (provider) {
        eventData.provider_id = provider.id;
        eventData.provider_name = provider.name.full ?? provider.name.full_name;

        if (provider.specialties) {
          eventData.provider_specialties = provider.specialties.map(
            (s) => s.name
          );
        }
      }

      log(BOOKING_SUCCESS, eventData);

      try {
        if (config.cerner_db_route_to) {
          // delayed to allow BOOKING_SUCCESS to go thru before the redirect
          setTimeout(
            () =>
              window.$HL.App.routeTo({
                path: config.cerner_db_route_to
              }),
            1000
          );
        }

        if (crmEnabled && crmConnector?.sendBookingData) {
          crmConnector.sendBookingData({
            appointment,
            location,
            provider,
            searchSummary
          });
        }
      } catch (e) {
        //nothing to do here
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, searchSummary, crmConnector?.sendBookingData, ehrData === null]
  );

  const handleScroll = useCallback(() => {
    if (onScroll) {
      // this scroll function is passed from the drawer route
      onScroll();
    } else {
      // if we're not in the drawer, do the normal scroll action (also handles scrollig in CCF)
      handleScrollToTop();
    }
  }, [onScroll]);

  // Extract the appointment EHR ID from the URL query params.
  // Pass it as a prop to Direct Booking for appointment booking.
  // This is only used in the case of cerner_embedded_order_appts, where we need to pass the appointment ID
  const appointmentEhrId = useMemo(
    () => getAppointmentEhrId(location),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // We only want to extract the appointment EHR ID once on initial render
  );

  // Check if we are using react-router client-side modal routing
  const isClientSideModalRouting =
    location.state?.provider?.provider?.id != null;

  /* DBW can be configured to render within a drawer.
  Do not render DBW if the required parameters are not provided. */
  if (
    !isClient ||
    (shouldRenderDirectBookInDrawer(config) && !isClientSideModalRouting)
  ) {
    return null;
  }
  /*
    If using react-router client-side modal routing, use the provider ID from the location state.
    Otherwise, use the value from the URL params.
  */
  const providerId = isClientSideModalRouting
    ? location.state.provider.provider.id
    : match.params.providerId;
  /*
      If using react-router client-side modal routing, do not use DBW's internal history routing.
      History routing will perform server-side routing causing loss of client-side context.
    */
  const historyObj = isClientSideModalRouting ? undefined : history;

  // Determines whether appointment times will be rendered in the `default` vertical list or a `dense` multi-column list
  const apptDensity = config.direct_book?.enable_dense_appointment_list
    ? 'dense'
    : 'default';

  // If skip is set to true, pass null. Otherwise, pass property name to check based on whether we're in PMC or PMAC.
  const dbEnabledProperty = config.skip_db_enablement_check
    ? null
    : isModuleEnabled(config, MODULES.VISIBILITY_AGENT)
      ? PMAC_DBW_ENABLED_PROPERTY
      : PMC_DBW_ENABLED_PROPERTY;

  const renderDirectBook = () => {
    if (!directBookProps) {
      return null;
    }

    if (
      config?.darkship_feature_flags &&
      config?.darkship_feature_flags?.use_direct_book_v2
    ) {
      return (
        <DirectBookV2
          track={log}
          fetchFromScheduling={true}
          csrfToken={customerCode}
          config={config.direct_book}
          darkshipFeatureFlags={config.darkship_feature_flags}
          searchMatchEnabled={isModuleEnabled(config, MODULES.SEARCH_MATCH)}
          featureFlags={config?.feature_flags}
          providerId={providerId}
          onDidBook={handleDidBook}
          history={historyObj}
          visibility={config.visibility}
          apptDensity="dense"
          dbEnabledProperty={dbEnabledProperty}
          apptPurposeFilterProps={getApptPurposeFilterProps({
            intl,
            config
          })}
          onScroll={handleScroll}
          productName={productName}
          awsMapConfig={{
            identityPoolId: appSettings.AWS_MAPS_CREDENTIALS.mapIdentityPoolId,
            mapName: appSettings.AWS_MAPS_CREDENTIALS.mapName,
            region: appSettings.AWS_MAPS_CREDENTIALS.mapRegion
          }}
          appointmentEhrId={appointmentEhrId}
          {...directBookProps}
        />
      );
    }

    return (
      <DirectBook
        track={log}
        fetchFromScheduling={true}
        csrfToken={customerCode}
        config={config.direct_book}
        providerId={providerId}
        onDidBook={handleDidBook}
        history={historyObj}
        visibility={config.visibility}
        apptDensity={apptDensity}
        dbEnabledProperty={dbEnabledProperty}
        apptPurposeFilterProps={getApptPurposeFilterProps({
          intl,
          config
        })}
        onScroll={handleScroll}
        productName={productName}
        awsMapConfig={{
          identityPoolId: appSettings.AWS_MAPS_CREDENTIALS.mapIdentityPoolId,
          mapName: appSettings.AWS_MAPS_CREDENTIALS.mapName,
          region: appSettings.AWS_MAPS_CREDENTIALS.mapRegion
        }}
        {...directBookProps}
      />
    );
  };

  return (
    <DirectBookContainer
      className="kyruus-custom direct-book-container"
      data-dd-privacy="mask" /* mask all strings in DBW from RUM replays */
      jumpToDateEnabled={!!config.direct_book.darkship_use_jump_to_date}
    >
      {renderDirectBook()}
    </DirectBookContainer>
  );
}

DirectBooking.propTypes = propTypes;

export default injectIntl(DirectBooking);
